/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { DateTime } from 'luxon';

import { Mark } from '@mui/base';

import { ServiceType } from 'shared';

import { CLUSTERGW_DEFAULT_DATABASE, DATASET_DEFAULT_READONLY } from '../../constants/settings';
import { ClusterCreateRequestBody } from '../../types/cluster/ClusterCreateRequestBody';
import {
  CreateClusterFromBackupValues,
  CreateClusterOverlayMapType,
  CreateClusterOverlaySteps,
  CreateClusterValues,
  Weekday,
} from './CreateClusterOverlay.types';

export const createClusterFormOverlayMap = (
  serviceType: ServiceType,
  isDataSetsAvailable: boolean,
): CreateClusterOverlayMapType<CreateClusterOverlaySteps> => {
  switch (serviceType) {
    case 'postgres':
    case 'pgee':
      return {
        [CreateClusterOverlaySteps.STEP_INFO]: {
          stepNumber: 1,
          nextStep: CreateClusterOverlaySteps.STEP_SETTINGS,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
        [CreateClusterOverlaySteps.STEP_SETTINGS]: {
          stepNumber: 2,
          nextStep: CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
        [CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP]: {
          stepNumber: 3,
          nextStep: CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS,
          prevStep: CreateClusterOverlaySteps.STEP_SETTINGS,
        },
        [CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS]: {
          stepNumber: 4,
          nextStep: isDataSetsAvailable
            ? CreateClusterOverlaySteps.STEP_DATA_SETS
            : CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP,
        },
        [CreateClusterOverlaySteps.STEP_DATA_SETS]: {
          stepNumber: isDataSetsAvailable ? 5 : null,
          nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS,
        },
        [CreateClusterOverlaySteps.STEP_SUPPORT_PLAN]: {
          stepNumber: isDataSetsAvailable ? 6 : 5,
          nextStep: CreateClusterOverlaySteps.STEP_SUMMARY,
          prevStep: isDataSetsAvailable
            ? CreateClusterOverlaySteps.STEP_DATA_SETS
            : CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS,
        },
        [CreateClusterOverlaySteps.STEP_SUMMARY]: {
          stepNumber: isDataSetsAvailable ? 7 : 6,
          nextStep: CreateClusterOverlaySteps.STEP_SUMMARY,
          prevStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
        },
        [CreateClusterOverlaySteps.STEP_BACKUP]: {
          stepNumber: null,
          nextStep: CreateClusterOverlaySteps.STEP_INFO,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
      };
    case 'cypex':
      return {
        [CreateClusterOverlaySteps.STEP_INFO]: {
          stepNumber: 1,
          nextStep: CreateClusterOverlaySteps.STEP_SETTINGS,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
        [CreateClusterOverlaySteps.STEP_SETTINGS]: {
          stepNumber: 2,
          nextStep: CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
        [CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP]: {
          stepNumber: 3,
          nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_SETTINGS,
        },
        [CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS]: {
          stepNumber: null,
          nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP,
        },
        [CreateClusterOverlaySteps.STEP_DATA_SETS]: {
          stepNumber: null,
          nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP,
        },
        [CreateClusterOverlaySteps.STEP_SUPPORT_PLAN]: {
          stepNumber: 4,
          nextStep: CreateClusterOverlaySteps.STEP_SUMMARY,
          prevStep: CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP,
        },
        [CreateClusterOverlaySteps.STEP_SUMMARY]: {
          stepNumber: 5,
          nextStep: CreateClusterOverlaySteps.STEP_SUMMARY,
          prevStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
        },
        [CreateClusterOverlaySteps.STEP_BACKUP]: {
          stepNumber: null,
          nextStep: CreateClusterOverlaySteps.STEP_INFO,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
      };
    case 'migrator':
    case 'babelfish':
      return {
        [CreateClusterOverlaySteps.STEP_INFO]: {
          stepNumber: 1,
          nextStep: CreateClusterOverlaySteps.STEP_SETTINGS,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
        [CreateClusterOverlaySteps.STEP_SETTINGS]: {
          stepNumber: 2,
          nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
        [CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP]: {
          stepNumber: null,
          nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_SETTINGS,
        },
        [CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS]: {
          stepNumber: null,
          nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_SETTINGS,
        },
        [CreateClusterOverlaySteps.STEP_DATA_SETS]: {
          stepNumber: null,
          nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
          prevStep: CreateClusterOverlaySteps.STEP_SETTINGS,
        },
        [CreateClusterOverlaySteps.STEP_SUPPORT_PLAN]: {
          stepNumber: 3,
          nextStep: CreateClusterOverlaySteps.STEP_SUMMARY,
          prevStep: CreateClusterOverlaySteps.STEP_SETTINGS,
        },
        [CreateClusterOverlaySteps.STEP_SUMMARY]: {
          stepNumber: 4,
          nextStep: CreateClusterOverlaySteps.STEP_SUMMARY,
          prevStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
        },
        [CreateClusterOverlaySteps.STEP_BACKUP]: {
          stepNumber: null,
          nextStep: CreateClusterOverlaySteps.STEP_INFO,
          prevStep: CreateClusterOverlaySteps.STEP_INFO,
        },
      };
  }
};

export const createClusterFormFromBackupOverlayMap = (
  isDataSetsAvailable: boolean,
): CreateClusterOverlayMapType<CreateClusterOverlaySteps> => ({
  [CreateClusterOverlaySteps.STEP_BACKUP]: {
    stepNumber: 1,
    nextStep: CreateClusterOverlaySteps.STEP_INFO,
    prevStep: CreateClusterOverlaySteps.STEP_BACKUP,
  },
  [CreateClusterOverlaySteps.STEP_INFO]: {
    stepNumber: 2,
    nextStep: CreateClusterOverlaySteps.STEP_SETTINGS,
    prevStep: CreateClusterOverlaySteps.STEP_BACKUP,
  },
  [CreateClusterOverlaySteps.STEP_SETTINGS]: {
    stepNumber: 3,
    nextStep: CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP,
    prevStep: CreateClusterOverlaySteps.STEP_INFO,
  },
  [CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP]: {
    stepNumber: 4,
    nextStep: CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS,
    prevStep: CreateClusterOverlaySteps.STEP_SETTINGS,
  },
  [CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS]: {
    stepNumber: 5,
    nextStep: isDataSetsAvailable
      ? CreateClusterOverlaySteps.STEP_DATA_SETS
      : CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
    prevStep: CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP,
  },
  [CreateClusterOverlaySteps.STEP_DATA_SETS]: {
    stepNumber: isDataSetsAvailable ? 6 : null,
    nextStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
    prevStep: CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS,
  },
  [CreateClusterOverlaySteps.STEP_SUPPORT_PLAN]: {
    stepNumber: isDataSetsAvailable ? 7 : 6,
    nextStep: CreateClusterOverlaySteps.STEP_SUMMARY,
    prevStep: isDataSetsAvailable
      ? CreateClusterOverlaySteps.STEP_DATA_SETS
      : CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS,
  },
  [CreateClusterOverlaySteps.STEP_SUMMARY]: {
    stepNumber: isDataSetsAvailable ? 8 : 7,
    nextStep: CreateClusterOverlaySteps.STEP_SUMMARY,
    prevStep: CreateClusterOverlaySteps.STEP_SUPPORT_PLAN,
  },
});

export const createSliderMarks = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, index) => ({
    value: min + index,
    label: String(min + index),
  })) as Mark[];

const WEEKDAY_REGEX = '(Mon|Tue|Wed|Thu|Fri|Sat|Sun)?';
const TIME_RANGE_REGEX = '(\\d{2}:\\d{2})-(\\d{2}:\\d{2})';
const MAINTENANCE_WINDOW_REGEX = new RegExp(`^${WEEKDAY_REGEX}:?${TIME_RANGE_REGEX}$`);

export const parseMaintenanceWindow = (maintenanceWindow: string) => {
  const match = maintenanceWindow.match(MAINTENANCE_WINDOW_REGEX);
  const [, weekdayMatch, startMatch, endMatch] = match!;

  const weekday: Weekday = (weekdayMatch || '') as Weekday;
  const startTimeUtc = DateTime.fromFormat(startMatch, 'HH:mm', { zone: 'utc' });
  const endTimeUtc = DateTime.fromFormat(endMatch, 'HH:mm', { zone: 'utc' });

  return { weekday, startTimeUtc, endTimeUtc };
};

export const formatMaintenanceWindow = ({
  weekday,
  startTimeUtc,
  endTimeUtc,
}: {
  weekday: Weekday;
  startTimeUtc: DateTime;
  endTimeUtc: DateTime;
}) => {
  const formattedStart = startTimeUtc.toFormat('HH:mm');
  const formattedEnd = endTimeUtc.toFormat('HH:mm');

  return !weekday ? `${formattedStart}-${formattedEnd}` : `${weekday}:${formattedStart}-${formattedEnd}`;
};

export const createClusterRequest = (
  values: CreateClusterValues | CreateClusterFromBackupValues,
  serviceType: ServiceType,
  isBackup = false,
) => {
  const basicInfo = {
    name: values.name.trim(),
    supportPlanId: values.supportPlanId.trim(),
    replicas: values.replicas,
    description: values.description?.trim() || undefined,
    allowedSourceRanges: values.allowAllConnections ? null : values.allowedSourceRanges,
    postgresVersion: values.postgresVersion,
    service: { type: serviceType },
    backupConfiguration: {
      logical: {
        enabled: values.backupConfigurationLogicalEnabled,
        schedule: values.backupConfigurationLogicalSchedule,
        daysToRetain: Number(values.backupConfigurationLogicalDaysToRetain),
      },
      continuous: {
        enabled: values.backupConfigurationContinuousEnabled,
        schedule: values.backupConfigurationContinuousSchedule,
        daysToRetain: Number(values.backupConfigurationContinuousDaysToRetain),
      },
    },
    maintenanceWindows: values.maintenanceWindows.map(formatMaintenanceWindow),
    datasets: values.selectedDataSetIds.map(datasetId => ({
      datasetId,
      database: CLUSTERGW_DEFAULT_DATABASE,
      readonly: values.dataSetsMetas[datasetId]?.readonly ?? DATASET_DEFAULT_READONLY,
    })),
  };

  const clusterBody: ClusterCreateRequestBody = {
    ...basicInfo,
    ...{
      cpuUnits: values.cpuUnits,
      storageGbs: values.storageGbs,
      memoryGbs: values.memoryGbs,
      storageClassId: values.storageClassId,
    },
    ...(isBackup && {
      restore: {
        timestamp: (values as CreateClusterFromBackupValues).backupTimestamp!.toISOString(),
        organizationId: (values as CreateClusterFromBackupValues).backupOrganizationId!,
        projectId: (values as CreateClusterFromBackupValues).backupProjectId!,
        clusterId: (values as CreateClusterFromBackupValues).backupClusterId!,
        cloneAcl: (values as CreateClusterFromBackupValues).cloneAcl,
        cloneContinuousBackupConfiguration: (values as CreateClusterFromBackupValues)
          .cloneContinuousBackupConfiguration,
        cloneLogicalBackupConfiguration: (values as CreateClusterFromBackupValues).cloneLogicalBackupConfiguration,
        cloneParameters: (values as CreateClusterFromBackupValues).cloneParameters,
      },
    }),
  };

  return clusterBody;
};
